import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Download, Heart, Camera, ChevronLeft, ChevronRight, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
// Avatar imports not needed on tiles after removing creator row
import { cn } from '@/lib/utils';
import { Sequence } from '@/types/sequence';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import AuthModal from '@/components/auth/AuthModal';
import { useFavoritesStore } from '@/stores/useFavoritesStore';
import { useToastStore } from '@/stores/useToastStore';
import { formatSequencePrice } from '@/utils/formatters';
import { layoutPhotoService } from '@/services/layoutPhotoService';
import SpotifyPreviewPlayer from '@/components/shared/SpotifyPreviewPlayer';

interface SequenceCardProps {
  sequence: Sequence;
}

const SAINT_NICK_USER_ID = 'f47ac10b-58cc-4372-a567-0e02b2c3d479';

export const SequenceCard: React.FC<SequenceCardProps> = ({
  sequence
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [currentRating, setCurrentRating] = useState(sequence.averageRating || sequence.rating || 0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [layoutPhotos, setLayoutPhotos] = useState<any[]>([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showLayoutPhotos, setShowLayoutPhotos] = useState(false);
  const [showSpotifyPlayer, setShowSpotifyPlayer] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const { addToFavorites, removeFromFavorites, isFavorite: isFavoriteStore } = useFavoritesStore();
  const addToast = useToastStore((state) => state.addToast);

  const hasSpotifyPreview = !!(sequence.spotify_preview_url || sequence.song?.spotifyPreviewUrl);

  // Fetch layout photos
  useEffect(() => {
    const fetchLayoutPhotos = async () => {
      try {
        const photos = await layoutPhotoService.getLayoutPhotos(sequence.id);
        if (photos.length > 0) {
          setLayoutPhotos(photos);
        }
      } catch (error) {
        // Silently handle error - photos are optional
      }
    };

    fetchLayoutPhotos();
  }, [sequence.id]);
  
  // Image URL fallback logic
  const getImageUrl = () => {
    // First check for direct imageUrl
    if (sequence.imageUrl && sequence.imageUrl.trim() !== '') {
      return sequence.imageUrl;
    }
    
    // Check for album cover URL from sequences table
    if (sequence.album_cover_url && sequence.album_cover_url.trim() !== '') {
      return sequence.album_cover_url;
    }
    
    // Check if sequence has song imageUrl
    if (sequence.song?.imageUrl && sequence.song.imageUrl.trim() !== '') {
      return sequence.song.imageUrl;
    }
    
    // Return null for placeholder
    return null;
  };

  const imageUrl = getImageUrl();

  const handleImageError = () => {
    // Image error handled silently - logging removed to reduce console noise
    setImageLoadError(true);
  };

  const handleImageLoad = () => {
    setIsLoaded(true);
    setImageLoadError(false);
  };

  const isFree = sequence.isFree || sequence.price === 0 || sequence.price == null;
  const isFavorite = isFavoriteStore(sequence.id);

  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    if (isFavorite) {
      removeFromFavorites(sequence.id);
      addToast({
        title: 'Removed from favorites',
        description: `${sequence.title || 'Sequence'} has been removed from your favorites`,
        type: 'info'
      });
    } else {
      addToFavorites({
        id: sequence.id,
        title: sequence.title || 'Untitled Sequence',
        imageUrl: imageUrl || '',
        type: 'sequence'
      });
      addToast({
        title: 'Added to favorites',
        description: `${sequence.title || 'Sequence'} has been added to your favorites`,
        type: 'success'
      });
    }
  };

  const handleAuthSuccess = () => {
    // After successful auth, trigger the favorite action
    addToFavorites({
      id: sequence.id,
      title: sequence.title || 'Untitled Sequence',
      imageUrl: imageUrl || '',
      type: 'sequence'
    });
    toast({
      title: "Added to favorites",
      description: "Sequence added to your favorites"
    });
  };

  const handleRatingUpdate = (newRating: number) => {
    setCurrentRating(newRating);
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (layoutPhotos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev + 1) % layoutPhotos.length);
    }
  };

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (layoutPhotos.length > 0) {
      setCurrentPhotoIndex((prev) => (prev - 1 + layoutPhotos.length) % layoutPhotos.length);
    }
  };

  const togglePhotoView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (layoutPhotos.length > 0) {
      setShowLayoutPhotos(!showLayoutPhotos);
      if (!showLayoutPhotos) {
        setCurrentPhotoIndex(0);
      }
    }
  };

  const placeholderImage = (
    <div className="w-full h-full flex items-center justify-center bg-muted">
      <div className="text-center p-4">
        <img 
          src="/sequence-cover-placeholder.svg" 
          alt="Sequence cover placeholder" 
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );

  return (
    <>
      <div 
        className="group relative rounded-xl overflow-hidden bg-card shadow-xs hover:shadow-md transition-all duration-300"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Album Cover Container */}
        <div className="aspect-square w-full overflow-hidden relative">
          {/* Initial blur overlay while image loads */}
          <div 
            className={cn(
              "absolute inset-0 bg-muted backdrop-blur-xs z-10 transition-opacity duration-500",
              isLoaded ? "opacity-0" : "opacity-100"
            )}
          />
          
          {showLayoutPhotos && layoutPhotos.length > 0 ? (
            /* Layout Photos Display */
            <>
              <img
                src={layoutPhotos[currentPhotoIndex].photo_url}
                alt={`Layout photo ${currentPhotoIndex + 1}`}
                className={cn(
                  "w-full h-full object-cover transition-all duration-700",
                  isHovered ? "scale-110" : "scale-100"
                )}
                loading="lazy"
              />
              {/* Photo Navigation */}
              {layoutPhotos.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-30 h-8 w-8 rounded-full bg-black/50 backdrop-blur-xs hover:bg-black/70 text-white"
                    onClick={handlePrevPhoto}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-30 h-8 w-8 rounded-full bg-black/50 backdrop-blur-xs hover:bg-black/70 text-white"
                    onClick={handleNextPhoto}
                  >
                    <ChevronRight size={16} />
                  </Button>
                  {/* Photo Indicators */}
                  <div className="absolute bottom-14 left-1/2 -translate-x-1/2 z-30 flex gap-1">
                    {layoutPhotos.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "h-1.5 rounded-full transition-all duration-300",
                          index === currentPhotoIndex
                            ? "w-6 bg-white"
                            : "w-1.5 bg-white/60"
                        )}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : imageUrl && !imageLoadError ? (
            /* Sequence Cover with loading effect */
            <img
              src={imageUrl}
              alt={`${sequence.title || 'Sequence'} cover`}
              className={cn(
                "w-full h-full object-cover transition-all duration-700",
                isLoaded ? "scale-100 filter-none" : "scale-105 blur-xs",
                isHovered ? "scale-110" : "scale-100"
              )}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
          ) : placeholderImage}

          {/* Software Badge */}
          <Badge 
            className="absolute top-3 left-3 z-30 bg-white/20 backdrop-blur-xs text-white border-none"
          >
            {sequence.software_type || sequence.software}
          </Badge>
          
          {/* Action Buttons */}
          <div className="absolute top-3 right-3 z-30 flex gap-2">
            {/* Layout Photos Toggle */}
            {layoutPhotos.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "rounded-full backdrop-blur-xs hover:bg-black/30 text-white",
                  showLayoutPhotos ? "bg-blue-500/50" : "bg-black/20"
                )}
                onClick={togglePhotoView}
                title={showLayoutPhotos ? "Show album art" : "Show layout photos"}
              >
                <Camera size={18} />
              </Button>
            )}
            {/* Favorite Button */}
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full bg-black/20 backdrop-blur-xs hover:bg-black/30 text-white"
              onClick={handleFavorite}
            >
              <Heart
                size={18}
                className={cn(
                  "transition-all",
                  isFavorite ? "fill-destructive stroke-destructive" : "fill-none"
                )}
              />
            </Button>
          </div>
          
          {/* Price Tag */}
          <div className="absolute bottom-3 right-3 z-30">
            <Badge 
              className={cn(
                "backdrop-blur-xs border-none",
                isFree
                  ? "bg-green-500/80 text-white" 
                  : "bg-white/20 text-white"
              )}
            >
              {isFree ? 'Free Sequence' : formatSequencePrice(sequence.price ?? 0)}
            </Badge>
          </div>

          {/* Hover overlay */}
          <div 
            className={cn(
              "absolute inset-0 bg-black/0 transition-all duration-300",
              isHovered ? "bg-black/30" : ""
            )}
          />
        </div>

        {/* Content */}
        <div className="p-3">
          <div className="mb-1">
            <h3 className="font-medium text-base line-clamp-1">{sequence.title}</h3>
          </div>

          {/* Song Details with inline downloads */}
          <div className="mt-1 mb-2">
            <div className="flex items-center gap-2">
              <Music size={12} className="text-muted-foreground shrink-0" />
              <p className="text-xs text-muted-foreground line-clamp-1">
                {sequence.song?.artist || 'Unknown Artist'}
              </p>
              {(sequence.downloads || sequence.downloadCount || 0) > 0 && (
                <>
                  <span className="text-muted-foreground text-xs">•</span>
                  <Download size={12} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{sequence.downloads || sequence.downloadCount}</span>
                </>
              )}
            </div>
          </div>

          {/* Spotify Preview Button */}
          {hasSpotifyPreview && (
            <div className="mt-2 mb-2">
              <Button
                size="sm"
                variant="outline"
                className="w-full h-8 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowSpotifyPlayer(!showSpotifyPlayer);
                }}
              >
                <Headphones size={14} className="mr-1.5" />
                {showSpotifyPlayer ? 'Hide Preview' : 'Preview Audio'}
              </Button>
            </div>
          )}

          {/* Spotify Preview Player */}
          {showSpotifyPlayer && hasSpotifyPreview && (
            <div className="mt-2 pt-2 border-t" onClick={(e) => e.stopPropagation()}>
              <SpotifyPreviewPlayer
                previewUrl={sequence.spotify_preview_url || sequence.song?.spotifyPreviewUrl || ''}
                trackName={sequence.title || ''}
                artistName={sequence.song?.artist || 'Unknown Artist'}
                albumCoverUrl={sequence.album_cover_url || sequence.song?.imageUrl}
                compact={true}
              />
            </div>
          )}

          {/* Creator row removed per request; downloads shown above next to artist */}
        </div>
        
        {/* Clickable overlay for right-click support */}
        <Link 
          to={`/sequence/${sequence.id}`} 
          className="absolute inset-0 z-20" 
          aria-label={`View ${sequence.title || 'sequence'}`}
        />
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        title="Sign in to save favorites"
        description="Sign in to save this sequence to your favorites and access it later."
      />
    </>
  );
};
